import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { Donut } from '../../components/charts/Donut.js'
import { LineChart } from '../../components/charts/LineChart.js'
import { Metric } from '../../components/Metric.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatDecimal, formatRate } from '../../lib/format.js'
import { fromInputDate, PERIOD_OPTIONS, toInputDate, usePeriod } from '../../lib/period.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Button, Input, Text } from '../../ui/index.js'
import { CycleTimePanel, DeadlineRealismPanel, LeaveAdjustedPanel, ReworkPanel, ReviewLatencyPanel, ThroughputPanel, WorkloadPanel } from './InsightPanels.js'

interface Summary {
  readonly avgEfficiency: number
  readonly avgComplexity: number
  readonly onTimeRate: number
  readonly returnedForCorrection: number
}

interface Department {
  readonly id: string
  readonly name: string
}

export interface PersonRow {
  readonly userId: string
  readonly name: string
  readonly departmentId: string | null
  readonly tasks: number
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly avgComplexity: number
  readonly score: number | null
  readonly belowMinimumSample: boolean
}

interface DepartmentRow {
  readonly departmentId: string
  readonly name: string
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly score: number | null
}

interface TrendPoint {
  readonly from: string
  readonly to: string
  readonly score: number | null
  readonly onTime: number | null
  readonly firstTimePass: number | null
}

interface TrendResponse {
  readonly points: readonly TrendPoint[]
}

interface CompareTotals {
  readonly avgEfficiency: number
  readonly avgComplexity: number
  readonly onTimeRate: number
  readonly returnedForCorrection: number
  readonly closedTasks: number
}

interface CompareResponse {
  readonly current: CompareTotals
  readonly previous: CompareTotals
  readonly suppressed: boolean
}

function latestValue<T>(items: readonly T[], readValue: (item: T) => number | null | undefined): number | undefined {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const value = readValue(items[index]!)
    if (value !== null && value !== undefined) return value
  }
  return undefined
}

function sortPeople(rows: readonly PersonRow[]): PersonRow[] {
  return [...rows].sort((a, b) => {
    if (a.belowMinimumSample !== b.belowMinimumSample) return a.belowMinimumSample ? 1 : -1
    if (a.score === null && b.score === null) return a.name.localeCompare(b.name)
    if (a.score === null) return 1
    if (b.score === null) return -1
    if (a.score !== b.score) return b.score - a.score
    return a.name.localeCompare(b.name)
  })
}

function tierLabel(row: PersonRow): string {
  if (row.belowMinimumSample || row.score === null) return 'Not enough data'
  if (row.score >= 85) return 'On track'
  if (row.score >= 75) return 'Steady'
  return 'Needs support'
}

function tierTone(row: PersonRow): 'default' | 'pos' | 'warn' {
  if (row.belowMinimumSample || row.score === null) return 'warn'
  if (row.score >= 85) return 'pos'
  return 'default'
}

/**
 * Exported for direct unit testing. The DOM test that used to cover these branches asserted
 * "click a person -> the panel updates", which M12 made impossible: clicking now navigates to
 * that person's own page. Testing the pure function keeps the four branches covered without
 * depending on a selection interaction that no longer exists.
 */
export function coachingNote(selected: PersonRow, topRanked: PersonRow | null): string {
  if (selected.belowMinimumSample || selected.score === null) {
    return `${selected.name} does not have enough data yet to coach from the ranking.`
  }
  if (selected.score >= 85) {
    return `${selected.name} is in the stretch band. Keep the momentum with a high-complexity assignment.`
  }
  if (selected.score >= 75) {
    return topRanked === null
      ? `${selected.name} is in the pairing band. Pair the next review with a strong teammate.`
      : `${selected.name} is in the pairing band. Pair the next review with ${topRanked.name}.`
  }
  return `${selected.name} needs a 1-on-1 before the next heavy assignment.`
}

/** Filename-safe person name: `Amara Okafor` -> `amara-okafor`. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function ManagerAnalytics() {
  usePageTitle('Analytics')
  const { api } = useAuth()
  const navigate = useNavigate()
  const [scopeId, setScopeId] = useState<string | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  const {
    period,
    setPeriod,
    from,
    to,
    bucket,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    isValidRange,
  } = usePeriod('quarter')
  // Analytics has always offered only the two long presets; M12 adds Custom beside them
  // so a manager can report on any period here too, without reintroducing week/month.
  const invalidCustomRange = period === 'custom' && !isValidRange

  const departmentsQuery = useQuery({
    queryKey: ['analytics-departments'],
    queryFn: () => api.get<Department[]>('/api/departments'),
  })

  const scopeSuffix = scopeId === null ? '' : `&departmentId=${encodeURIComponent(scopeId)}`

  const summaryQuery = useQuery({
    queryKey: ['analytics-summary', from, to, scopeId],
    queryFn: () => api.get<Summary>(`/api/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const peopleQuery = useQuery({
    queryKey: ['analytics-people', from, to, scopeId],
    queryFn: () => api.get<PersonRow[]>(`/api/analytics/people?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const departmentQuery = useQuery({
    queryKey: ['analytics-department-comparison', from, to, scopeId],
    queryFn: () => api.get<DepartmentRow[]>(`/api/analytics/departments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const compareQuery = useQuery({
    queryKey: ['analytics-compare', from, to, scopeId],
    retry: false,
    queryFn: () => api.get<CompareResponse>(`/api/analytics/compare?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${scopeSuffix}`),
  })

  const selectedPerson = useMemo(() => {
    const people = sortPeople(peopleQuery.data ?? [])
    return people.find((row) => row.userId === selectedPersonId) ?? people[0] ?? null
  }, [peopleQuery.data, selectedPersonId])

  useEffect(() => {
    if (selectedPerson === null) {
      if (selectedPersonId !== null) setSelectedPersonId(null)
      return
    }
    if (selectedPerson.userId !== selectedPersonId) {
      setSelectedPersonId(selectedPerson.userId)
    }
  }, [selectedPerson, selectedPersonId])

  const trendQuery = useQuery({
    queryKey: ['analytics-trend', from, to, bucket, selectedPerson?.userId ?? 'none', scopeId],
    enabled: selectedPerson !== null,
    queryFn: () =>
      api.get<TrendResponse>(
        `/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}&assigneeId=${encodeURIComponent(selectedPerson!.userId)}`,
      ),
  })

  const trendLoading = selectedPerson !== null && trendQuery.isPending
  const loading = departmentsQuery.isPending || summaryQuery.isPending || peopleQuery.isPending
  const failure = summaryQuery.error ?? peopleQuery.error
  const hasFailure = summaryQuery.isError || peopleQuery.isError

  if (loading) {
    return (
      <>
        <Topbar title="Analytics" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <LoadingState label="Loading analytics" />
          </div>
        </div>
      </>
    )
  }

  if (hasFailure) {
    return (
      <>
        <Topbar title="Analytics" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <ErrorState error={failure} onRetry={() => void summaryQuery.refetch()} />
          </div>
        </div>
      </>
    )
  }

  const departments = departmentsQuery.data ?? []
  const summary = summaryQuery.data!
  const people = sortPeople(peopleQuery.data ?? [])
  const departmentRows = departmentQuery.data ?? []
  const compare = compareQuery.data
  const compareAvailable = compare !== undefined && !compare.suppressed
  const avgEfficiencyDelta = compareAvailable ? compare.current.avgEfficiency - compare.previous.avgEfficiency : undefined
  const avgComplexityDelta = compareAvailable ? compare.current.avgComplexity - compare.previous.avgComplexity : undefined
  const onTimeDelta = compareAvailable ? Math.round((compare.current.onTimeRate - compare.previous.onTimeRate) * 100) : undefined
  const returnedDelta = compareAvailable ? Math.round((compare.previous.returnedForCorrection - compare.current.returnedForCorrection) * 100) : undefined
  const trend = trendQuery.data?.points ?? []
  const selectedScore = selectedPerson?.score ?? null
  const selectedTrend = trend
  const topRanked = people.find((row) => row.score !== null && !row.belowMinimumSample) ?? null
  const note = selectedPerson === null ? 'No ranking row selected yet.' : coachingNote(selectedPerson, topRanked)
  const deltaTone = (delta: number | undefined): 'default' | 'pos' | 'warn' | 'neg' => {
    if (delta === undefined) return 'default'
    if (delta > 0) return 'pos'
    if (delta < 0) return 'neg'
    return 'default'
  }
  const compareTrend = (previous: number, current: number): readonly (number | null)[] => [previous, current]

  return (
    <>
      <Topbar
        title="Analytics"
        eyebrow="Manager workspace"
        actions={
          <div style={{ display: 'flex', gap: 'var(--o-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <div role="group" aria-label="Scope selector" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                aria-pressed={scopeId === null}
                onClick={() => setScopeId(null)}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 999,
                  padding: '6px 12px',
                  background: scopeId === null ? 'var(--brand-soft)' : 'transparent',
                  color: scopeId === null ? 'var(--mauve-700)' : 'var(--fg-muted)',
                  fontSize: 12.5,
                  fontWeight: 500,
                }}
              >
                All
              </button>
              {departments.map((department) => (
                <button
                  key={department.id}
                  type="button"
                  aria-pressed={scopeId === department.id}
                  onClick={() => setScopeId(department.id)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background: scopeId === department.id ? 'var(--brand-soft)' : 'transparent',
                    color: scopeId === department.id ? 'var(--mauve-700)' : 'var(--fg-muted)',
                    fontSize: 12.5,
                    fontWeight: 500,
                  }}
                >
                  {department.name}
                </button>
              ))}
            </div>

            <div role="group" aria-label="Range selector" style={{ display: 'flex', gap: 8 }}>
              {PERIOD_OPTIONS.filter((option) => option.value === 'quarter' || option.value === 'year' || option.value === 'custom').map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={period === option.value}
                  onClick={() => setPeriod(option.value)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background: period === option.value ? 'var(--brand-soft)' : 'transparent',
                    color: period === option.value ? 'var(--mauve-700)' : 'var(--fg-muted)',
                    fontSize: 12.5,
                    fontWeight: 500,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {period === 'custom' ? (
              <div style={{ display: 'flex', gap: 'var(--o-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span className="card-label">From</span>
                  <Input
                    type="date"
                    aria-label="Range start"
                    value={customFrom === '' ? '' : toInputDate(customFrom)}
                    onChange={(value) => setCustomFrom(value === '' ? '' : fromInputDate(value))}
                    style={{ width: 170 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span className="card-label">To</span>
                  <Input
                    type="date"
                    aria-label="Range end"
                    value={customTo === '' ? '' : toInputDate(customTo)}
                    onChange={(value) => setCustomTo(value === '' ? '' : fromInputDate(value))}
                    style={{ width: 170 }}
                  />
                </label>
                {invalidCustomRange ? (
                  <Text color="secondary">Choose an end date after the start date to update the panels.</Text>
                ) : null}
              </div>
            ) : null}

            <Button
              kind="secondary"
              size="small"
              onClick={async () => {
                const query = new URLSearchParams({ from, to })
                if (scopeId !== null) query.set('departmentId', scopeId)
                const blob = await api.getBlob(`/api/reports/team.csv?${query.toString()}`)
                const url = URL.createObjectURL(blob)
                const anchor = document.createElement('a')
                anchor.href = url
                anchor.download = `ovrsee-team-${period}.csv`
                anchor.click()
                URL.revokeObjectURL(url)
              }}
            >
              Export CSV
            </Button>
            <Button
              kind="secondary"
              size="small"
              onClick={() => {
                const query = new URLSearchParams({ period })
                if (scopeId !== null) query.set('departmentId', scopeId)
                navigate(`/reports/print?${query.toString()}`)
              }}
            >
              Export PDF
            </Button>
          </div>
        }
      />

      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
          <div className="metric-grid">
            <Metric
              label="Avg efficiency"
              value={formatCount(summary.avgEfficiency)}
              unit="/100"
              delta={avgEfficiencyDelta}
              tone={deltaTone(avgEfficiencyDelta)}
              trend={compareAvailable ? compareTrend(compare.previous.avgEfficiency, compare.current.avgEfficiency) : null}
              footnote="from the selected scope"
            />
            <Metric
              label="Avg complexity"
              value={formatDecimal(summary.avgComplexity)}
              delta={avgComplexityDelta}
              tone={deltaTone(avgComplexityDelta)}
              trend={compareAvailable ? compareTrend(compare.previous.avgComplexity, compare.current.avgComplexity) : null}
              footnote="weighted task load"
            />
            <Metric
              label="On-time rate"
              value={formatRate(summary.onTimeRate)}
              delta={onTimeDelta}
              deltaSuffix="pp"
              tone={deltaTone(onTimeDelta)}
              trend={compareAvailable ? compareTrend(compare.previous.onTimeRate, compare.current.onTimeRate) : null}
              footnote="closed tasks only"
            />
            <Metric
              label="Returned for correction"
              value={formatRate(summary.returnedForCorrection)}
              delta={returnedDelta}
              deltaSuffix="pp"
              tone={deltaTone(returnedDelta)}
              trend={compareAvailable ? compareTrend(compare.previous.returnedForCorrection, compare.current.returnedForCorrection) : null}
              footnote="submitted tasks"
            />
          </div>

          <div className="split-grid split-grid--analytics">
            <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                <div>
                  <div className="card-label">Ranking</div>
                  <h3 style={{ margin: 0 }}>People ordered by efficiency</h3>
                </div>
                <Text color="secondary">Null scores and below-minimum rows stay at the bottom.</Text>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {people.map((row, index) => {
                  const isSelected = selectedPerson?.userId === row.userId
                  return (
                    <button
                      key={row.userId}
                      type="button"
                      onClick={() => {
                        setSelectedPersonId(row.userId)
                        navigate(`/analytics/person/${row.userId}`)
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '24px minmax(0, 1fr) auto',
                        alignItems: 'center',
                        gap: 'var(--o-space-3)',
                        width: '100%',
                        padding: 'var(--o-space-3)',
                        borderRadius: 'var(--r-md)',
                        border: isSelected ? '1px solid var(--mauve-200)' : '1px solid transparent',
                        background: isSelected ? 'var(--brand-soft)' : 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-muted)', textAlign: 'right' }}>
                        {index + 1}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                          {row.tasks} {row.tasks === 1 ? 'task' : 'tasks'} · complexity {formatDecimal(row.avgComplexity)}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: row.score === null || row.belowMinimumSample ? 'var(--fg-muted)' : row.score >= 85 ? 'var(--positive)' : row.score >= 75 ? 'var(--brand)' : 'var(--warning)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {tierLabel(row)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>

            <section style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                  <div>
                    <div className="card-label">Profile</div>
                    <h3 style={{ margin: 0 }}>{selectedPerson?.name ?? 'No person selected'}</h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--o-space-3)' }}>
                    <Text color="secondary">
                      {departments.find((d) => d.id === selectedPerson?.departmentId)?.name ?? 'No department'}
                    </Text>
                    {selectedPerson === null ? null : (
                      <Button
                        kind="tertiary"
                        size="small"
                        onClick={async () => {
                          // userId, not departmentId: this report is one person, and the
                          // API scopes it to the caller's visibility anyway.
                          const query = new URLSearchParams({ userId: selectedPerson.userId, from, to })
                          const blob = await api.getBlob(`/api/reports/person.csv?${query.toString()}`)
                          const url = URL.createObjectURL(blob)
                          const anchor = document.createElement('a')
                          anchor.href = url
                          anchor.download = `ovrsee-${slugify(selectedPerson.name)}-${period}.csv`
                          anchor.click()
                          URL.revokeObjectURL(url)
                        }}
                      >
                        Export person
                      </Button>
                    )}
                  </div>
                </div>

                {selectedPerson === null || selectedScore === null ? (
                  <EmptyState title="Not enough data" hint="Pick a person with enough closed work to open the drilldown." />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 'var(--o-space-4)', alignItems: 'center' }}>
                    <Donut
                      value={selectedScore}
                      max={100}
                      size={108}
                      stroke={10}
                      label={
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 26, fontWeight: 600 }}>{formatCount(selectedScore)}</div>
                          <div style={{ fontSize: 10, color: 'var(--fg-muted)' }}>EFFICIENCY</div>
                        </div>
                      }
                    />
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-2)' }}>
                          <div className="card-label">Tasks</div>
                          <div>{formatCount(selectedPerson.tasks)}</div>
                        </div>
                        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-2)' }}>
                          <div className="card-label">Avg complexity</div>
                          <div>{formatDecimal(selectedPerson.avgComplexity)}</div>
                        </div>
                        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-2)' }}>
                          <div className="card-label">On-time</div>
                          <div>{selectedPerson.onTime === null ? 'Not enough data' : formatRate(selectedPerson.onTime)}</div>
                        </div>
                        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-2)' }}>
                          <div className="card-label">First-time pass</div>
                          <div>{selectedPerson.firstTimePass === null ? 'Not enough data' : formatRate(selectedPerson.firstTimePass)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedPerson !== null ? (
                  <div style={{ marginTop: 'var(--o-space-4)' }}>
                    {trendQuery.isError ? (
                      <ErrorState error={trendQuery.error} onRetry={() => void trendQuery.refetch()} />
                    ) : (
                      <>
                        <div className="card-label">Trend</div>
                        <LineChart
                          width={520}
                          height={160}
                          yMax={100}
                          xLabels={selectedTrend.map((point) => point.from.slice(5, 10))}
                          series={[
                            { label: 'Score', data: selectedTrend.map((point) => point.score), color: 'var(--brand)', fill: true, dots: true },
                          ]}
                        />
                      </>
                    )}
                  </div>
                ) : null}

                {selectedPerson !== null ? (
                  <div style={{ marginTop: 'var(--o-space-4)', padding: 'var(--o-space-3)', borderRadius: 'var(--r-md)', background: 'var(--brand-soft)', border: '1px solid var(--mauve-200)' }}>
                    <div className="card-label">Coaching note</div>
                    <div style={{ lineHeight: 1.5 }}>{note}</div>
                    {selectedPerson.score !== null && selectedPerson.score >= 75 && selectedPerson.score < 85 && topRanked !== null ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--mauve-800)' }}>
                        Pairing name: {topRanked.name}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                  <div>
                    <div className="card-label">Department comparison</div>
                    <h3 style={{ margin: 0 }}>Scores by department</h3>
                  </div>
                  <Text color="secondary">Null scores show as Not enough data.</Text>
                </div>
                {departmentQuery.isError ? (
                  <ErrorState error={departmentQuery.error} onRetry={() => void departmentQuery.refetch()} />
                ) : (
                  <div className="dept-grid">
                    {departmentRows.map((department) => (
                      <div key={department.departmentId} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-3)' }}>
                        <div className="card-label">{department.name}</div>
                        <div style={{ fontSize: 28, fontWeight: 600, marginTop: 6 }}>
                          {department.score === null ? 'Not enough data' : formatCount(department.score)}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                          {department.score === null ? 'No score for the selected period' : 'department efficiency'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          <div style={{ display: 'grid', gap: 'var(--o-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <ThroughputPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <CycleTimePanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <ReviewLatencyPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <WorkloadPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <DeadlineRealismPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <ReworkPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
            <LeaveAdjustedPanel from={from} to={to} departmentId={scopeId} bucket={bucket} />
          </div>
        </div>
      </div>
    </>
  )
}
